import { STORE_LINKS } from '@/constants/site.constants';
import type {
  FaqItem,
  FeatureCard,
  HeroStat,
  HowItWorksStep,
  SiteLink,
  StoreBadge,
  TrustPoint,
} from '@/types/home.types';

export const SECTION_IDS = {
  howItWorks: 'how-it-works',
  features: 'features',
  trust: 'trust',
  faq: 'faq',
  download: 'get-the-app',
} as const;

export const HEADER_LINKS: SiteLink[] = [
  { label: 'How it works', href: `/#${SECTION_IDS.howItWorks}` },
  { label: 'Features', href: `/#${SECTION_IDS.features}` },
  { label: 'Trust & safety', href: `/#${SECTION_IDS.trust}` },
  { label: 'FAQ', href: `/#${SECTION_IDS.faq}` },
];

export const HEADER_CTA: SiteLink = {
  label: 'Get the app',
  href: `/#${SECTION_IDS.download}`,
};

export const FOOTER_PRODUCT_LINKS: SiteLink[] = [
  { label: 'How it works', href: `/#${SECTION_IDS.howItWorks}` },
  { label: 'Features', href: `/#${SECTION_IDS.features}` },
  { label: 'Trust & safety', href: `/#${SECTION_IDS.trust}` },
  { label: 'FAQ', href: `/#${SECTION_IDS.faq}` },
];

export const HERO_STATS: HeroStat[] = [
  { value: 'Verified', label: 'College email only' },
  { value: 'Deposit-backed', label: 'Held until return' },
  { value: 'Same campus', label: 'Hand-to-hand pickup' },
];

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    step: '01',
    title: 'Sign up with your college email',
    body: 'One OTP on your college address puts you on your campus. No strangers from across the city: only students from your own college can see and book your stuff.',
  },
  {
    step: '02',
    title: 'List it, or Shoutt for it',
    body: 'Have a camera, a cycle, a calculator, a blazer for tomorrow’s fest? List it with a per-day price. Need something instead? Post a Shoutt and let the campus answer.',
  },
  {
    step: '03',
    title: 'Book, meet, return',
    body: 'Pick your dates, pay the rent and the refundable deposit in the app, meet on campus to collect. Return it on time and the deposit comes straight back.',
  },
];

export const FEATURE_CARDS: FeatureCard[] = [
  {
    icon: '📦',
    title: 'Rent almost anything',
    body: 'Cameras, projectors, cycles, instruments, lab kits, formal wear, gaming consoles. If a student owns it, it can be rented for a day or a fortnight.',
  },
  {
    icon: '📣',
    title: 'Shoutts for what’s missing',
    body: 'Can’t find it in the feed? Post a Shoutt describing what you need and by when. Anyone on campus who has it can reply straight away.',
  },
  {
    icon: '🪙',
    title: 'Coins that actually pay off',
    body: 'Earn coins for completing your profile, your first rental, honest reviews and referrals. Spend them to mark a Shoutt urgent or knock down platform fees.',
  },
  {
    icon: '🛡️',
    title: 'A Trust Score you build',
    body: 'Every completed rental, on-time return and rating feeds a visible trust score, so both sides know exactly who they are dealing with before they meet.',
  },
  {
    icon: '💬',
    title: 'Chat built for handovers',
    body: 'Message inside the booking, agree a spot, and share live location while you walk over. No phone numbers handed to strangers.',
  },
  {
    icon: '💸',
    title: 'Transparent money',
    body: 'The full breakdown of rent, deposit, platform fee and GST is on screen before you pay. Deposits are released once the owner confirms the return.',
  },
];

export const TRUST_POINTS: TrustPoint[] = [
  {
    title: 'Campus-verified accounts',
    body: 'Every account is tied to a verified college email address and mapped to a campus. Profiles carry a name, department and year, not an anonymous handle.',
  },
  {
    title: 'Refundable security deposit',
    body: 'The borrower pays a deposit alongside the rent. It is held by Shoutt for the length of the booking and refunded after the owner confirms the item came back.',
  },
  {
    title: 'Damage reports and disputes',
    body: 'If something comes back broken, the owner files a damage report with photos. Our team reviews both sides before any deduction is made from the deposit.',
  },
  {
    title: 'Moderation that runs on every post',
    body: 'Listings and Shoutts are screened automatically for illegal, unsafe or scam content, and anything a student reports lands in a human review queue.',
  },
];

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'Who can join Shoutt?',
    answer:
      'Any student with a valid college email address at a campus we have opened. Your campus is detected from your email domain when you verify, and you only see listings from your own campus.',
  },
  {
    question: 'What does it cost?',
    answer:
      'Listing is free. When a booking is confirmed, the borrower pays the daily rent, a refundable security deposit and a platform fee (with GST charged on the fee only). Every number is shown before you pay.',
  },
  {
    question: 'How do I get my deposit back?',
    answer:
      'Return the item on the agreed date and the owner confirms the return in the app. The deposit is released back to you once that confirmation lands, unless a damage report is raised and upheld.',
  },
  {
    question: 'What if the item is damaged or not returned?',
    answer:
      'The owner raises a damage report with photos inside the booking. Our team reviews the evidence from both sides and decides what, if anything, is deducted from the deposit.',
  },
  {
    question: 'How do payments work?',
    answer:
      'Payments are made over UPI to Shoutt and confirmed by our team, so the money never passes directly between two students. Owners receive their payout after the rental is completed.',
  },
  {
    question: 'When is the app available?',
    answer:
      'Shoutt is rolling out campus by campus. The Android and iOS apps are on their way. Drop us a line if you want your college in the first wave.',
  },
];

export const STORE_BADGES: StoreBadge[] = [
  { platform: 'ios', storeName: 'App Store', prefix: 'Coming soon to the', href: STORE_LINKS.ios },
  {
    platform: 'android',
    storeName: 'Google Play',
    prefix: 'Coming soon to',
    href: STORE_LINKS.android,
  },
];
